import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { fmtDateTime, fmtTime } from '../lib/format'
import { useAuth } from '../lib/auth'
import { Card, Empty, Field } from './ui'

/**
 * The live-interaction half of Device detail (docs/10): test send, inbound inbox, AT console and
 * the raw frame trace. Kept out of DeviceDetail.tsx because that file is already the page shell
 * plus identity and routing config, and these four panels are one coherent debugging surface.
 *
 * Everything here polls. The fastest thing in the system is a 20 s heartbeat, so a socket to the
 * browser would buy nothing — except in the frame trace, which polls fast while capturing because
 * that is exactly when you are staring at it.
 */

// ---------------------------------------------------------------- test send

interface EncodingPreview {
  encoding: string
  partCount: number
  characters: number
}

export function TestSendCard({ deviceId, deviceName, connected }: { deviceId: string; deviceName: string; connected: boolean }) {
  const { can } = useAuth()
  const qc = useQueryClient()
  const [to, setTo] = useState('')
  const [text, setText] = useState('')
  const [sent, setSent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const preview = useQuery({
    queryKey: ['preview-encoding', text],
    queryFn: () => api.post<EncodingPreview>('/api/admin/preview-encoding', { text }),
    enabled: text.length > 0,
  })

  const send = useMutation({
    mutationFn: () =>
      api.post<{ messageId: string; parts: number; encoding: string; pinnedTo: string }>(
        `/api/admin/devices/${deviceId}/test-message`,
        { to, text },
      ),
    onSuccess: (r) => {
      setSent(`Queued ${r.parts} part${r.parts === 1 ? '' : 's'} (${r.encoding}) pinned to ${r.pinnedTo}.`)
      setError(null)
      setText('')
      void qc.invalidateQueries({ queryKey: ['device', deviceId] })
    },
    onError: (err) => {
      setSent(null)
      setError(err instanceof ApiError ? err.message : 'The send failed.')
    },
  })

  const disabled = !can('Operator') || !connected || !to.trim() || !text.trim() || send.isPending

  return (
    <Card title="Send a test SMS">
      <div className="stack" style={{ gap: 12 }}>
        <Field label="To" hint="Mauritian mobile number, any common format.">
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+230 5xxx xxxx" disabled={!can('Operator')} />
        </Field>

        <Field
          label="Message"
          hint={
            preview.data && preview.data.partCount > 0
              ? `${preview.data.characters} characters · ${preview.data.encoding.toUpperCase()} · ${preview.data.partCount} part${
                  preview.data.partCount === 1 ? '' : 's'
                }${preview.data.encoding === 'ucs2' ? ' — a non-GSM character forced UCS-2, which halves the characters per part' : ''}`
              : 'Encoding and part count are shown as you type.'
          }
        >
          <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} disabled={!can('Operator')} />
        </Field>

        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <button className="btn primary sm" onClick={() => send.mutate()} disabled={disabled}>
            {send.isPending ? 'Sending…' : `Send via ${deviceName}`}
          </button>
          <span className="faint small">
            Pinned to this device — if it cannot send, the message is refused rather than routed elsewhere.
          </span>
        </div>

        {sent && <div className="banner ok">{sent}</div>}
        {error && <div className="banner danger">{error}</div>}
        {!connected && <div className="field-hint">The device is offline, so nothing can be sent through it.</div>}
      </div>
    </Card>
  )
}

// ------------------------------------------------------------------ inbound

interface InboundRow {
  id: string
  fromMsisdn: string
  toMsisdn: string | null
  body: string
  receivedAt: string
  routingReason: string | null
  forwardStatus: string | null
  autoReplied: boolean
  routedSource: string | null
}

export function InboundCard({ deviceId }: { deviceId: string }) {
  const inbound = useQuery({
    queryKey: ['device-inbound', deviceId],
    queryFn: () => api.get<InboundRow[]>(`/api/admin/devices/${deviceId}/inbound`, { limit: 100 }),
    refetchInterval: 10_000,
  })

  return (
    <Card title="Received on this SIM" flush>
      {inbound.isLoading ? (
        <div className="pad">
          <span className="faint small">Loading…</span>
        </div>
      ) : !inbound.data || inbound.data.length === 0 ? (
        <Empty
          title="Nothing received yet"
          hint="Inbound messages appear here as the SIM receives them. Send an SMS to this device's own number to check the path end to end."
        />
      ) : (
        <div className="table-wrap" style={{ maxHeight: 380, overflow: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Received</th>
                <th>From</th>
                <th>Message</th>
                <th>Routed to</th>
              </tr>
            </thead>
            <tbody>
              {inbound.data.map((m) => (
                <tr key={m.id}>
                  <td className="faint small" style={{ whiteSpace: 'nowrap' }}>
                    {fmtDateTime(m.receivedAt)}
                  </td>
                  <td className="mono small" style={{ whiteSpace: 'nowrap' }}>
                    {m.fromMsisdn}
                  </td>
                  <td style={{ maxWidth: 420 }}>
                    {m.body}
                    {m.autoReplied && <span className="chip" style={{ marginLeft: 6 }}>auto-replied</span>}
                  </td>
                  <td className="small">
                    {m.routedSource ?? <span className="faint">unrouted</span>}
                    {m.routingReason && <div className="faint small mono">{m.routingReason}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

// --------------------------------------------------------------- AT console

/**
 * Mirrors the server's allow-list (docs/05 §6). Duplicated deliberately: this is the menu, the
 * server is the gate, and the server refuses anything not on its own list regardless of what the
 * console offers.
 */
const AT_COMMANDS: { cmd: string; what: string }[] = [
  { cmd: 'AT+CSQ', what: 'signal quality (RSSI, BER)' },
  { cmd: 'AT+CREG?', what: 'network registration' },
  { cmd: 'AT+COPS?', what: 'current operator' },
  { cmd: 'AT+CPIN?', what: 'SIM PIN state' },
  { cmd: 'AT+CCID', what: 'SIM ICCID' },
  { cmd: 'AT+CGMR', what: 'modem firmware revision' },
  { cmd: 'AT+CBC', what: 'battery / supply voltage' },
  { cmd: 'AT+CPMS?', what: 'message storage usage' },
  { cmd: 'AT+CSCA?', what: 'SMSC address' },
]

interface AtResult {
  id: string
  at: string
  summary: string
  data: string
  severity: string
}

export function AtConsoleCard({ deviceId, connected }: { deviceId: string; connected: boolean }) {
  const { can } = useAuth()
  const [cmd, setCmd] = useState(AT_COMMANDS[0].cmd)
  const [error, setError] = useState<string | null>(null)
  const [awaiting, setAwaiting] = useState(false)

  const results = useQuery({
    queryKey: ['device-at', deviceId],
    queryFn: () => api.get<AtResult[]>(`/api/admin/devices/${deviceId}/at-results`, { limit: 25 }),
    refetchInterval: awaiting ? 1_500 : 15_000,
    enabled: can('Owner'),
  })

  const latest = results.data?.[0]?.id
  const latestRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    // Stop the fast poll as soon as a new result lands, rather than after a fixed timer — the
    // modem's answer is the event we are waiting for.
    if (awaiting && latest && latest !== latestRef.current) setAwaiting(false)
  }, [awaiting, latest])

  const run = useMutation({
    mutationFn: () => api.post(`/api/admin/devices/${deviceId}/at`, { cmd }),
    onMutate: () => {
      latestRef.current = latest
      setError(null)
    },
    onSuccess: () => setAwaiting(true),
    onError: (err) => {
      setAwaiting(false)
      setError(err instanceof ApiError ? err.message : 'The command was refused.')
    },
  })

  if (!can('Owner')) {
    return (
      <Card title="AT console">
        <Empty title="Owner only" hint="Running AT commands against a live modem is restricted to the Owner role and is written to the audit log." />
      </Card>
    )
  }

  return (
    <Card title="AT console">
      <div className="stack" style={{ gap: 12 }}>
        <Field label="Command" hint="Read-only commands only. Anything that could send an SMS, change the SMSC or write NVRAM is not on the list.">
          <select value={cmd} onChange={(e) => setCmd(e.target.value)}>
            {AT_COMMANDS.map((c) => (
              <option key={c.cmd} value={c.cmd}>
                {c.cmd} — {c.what}
              </option>
            ))}
          </select>
        </Field>

        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <button className="btn sm" onClick={() => run.mutate()} disabled={!connected || run.isPending}>
            {run.isPending ? 'Sending…' : 'Run'}
          </button>
          {awaiting && <span className="faint small">Waiting for the modem…</span>}
          <span className="faint small">Every run is audited with your name.</span>
        </div>

        {error && <div className="banner danger">{error}</div>}
        {!connected && <div className="field-hint">The device is offline.</div>}

        {results.data && results.data.length > 0 && (
          <div className="table-wrap" style={{ maxHeight: 260, overflow: 'auto' }}>
            <table>
              <tbody>
                {results.data.map((r) => (
                  <tr key={r.id}>
                    <td className="faint small" style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      {fmtTime(r.at)}
                    </td>
                    <td>
                      <pre className="mono small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {r.summary || '(empty response)'}
                      </pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  )
}

// -------------------------------------------------------------- frame trace

interface FrameRow {
  seq: number
  at: string
  direction: 'in' | 'out'
  type: string
  id: string | null
  re: string | null
  bytes: number
  json: string
}

interface FramesResponse {
  capturing: boolean
  expiresAt: string | null
  frames: FrameRow[]
}

export function FrameTraceCard({ deviceId }: { deviceId: string }) {
  const { can } = useAuth()
  const [frames, setFrames] = useState<FrameRow[]>([])
  const [capturing, setCapturing] = useState(false)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  const cursor = useRef(0)

  const poll = useQuery({
    queryKey: ['device-frames', deviceId],
    queryFn: () => api.get<FramesResponse>(`/api/admin/devices/${deviceId}/frames`, { afterSeq: cursor.current }),
    // 1s while capturing: this is a live trace and the whole point is watching frames arrive.
    refetchInterval: capturing ? 1_000 : false,
    enabled: can('Admin'),
  })

  useEffect(() => {
    const data = poll.data
    if (!data) return
    setCapturing(data.capturing)
    setExpiresAt(data.expiresAt)
    if (data.frames.length > 0) {
      cursor.current = data.frames[data.frames.length - 1].seq
      // Bounded client-side too — a 30-minute capture on a chatty device would otherwise grow
      // the DOM without limit.
      setFrames((prev) => [...prev, ...data.frames].slice(-500))
    }
  }, [poll.data])

  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api.post<{ capturing: boolean; expiresAt: string | null }>(`/api/admin/devices/${deviceId}/capture`, {
        enabled,
        minutes: 15,
      }),
    onSuccess: (r) => {
      setCapturing(r.capturing)
      setExpiresAt(r.expiresAt)
      setError(null)
      if (r.capturing) void poll.refetch()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not change the capture.'),
  })

  if (!can('Admin')) {
    return (
      <Card title="Frame trace">
        <Empty title="Admin only" hint="The raw device link carries message content, so watching it requires the Admin role." />
      </Card>
    )
  }

  return (
    <Card
      title="Frame trace"
      actions={
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          {capturing && expiresAt && <span className="faint small">stops {fmtTime(expiresAt)}</span>}
          <button
            className={capturing ? 'btn danger sm' : 'btn sm'}
            onClick={() => toggle.mutate(!capturing)}
            disabled={toggle.isPending}
          >
            {capturing ? 'Stop capture' : 'Start capture (15 min)'}
          </button>
          {frames.length > 0 && (
            <button className="btn sm" onClick={() => setFrames([])}>
              Clear
            </button>
          )}
        </div>
      }
      flush
    >
      {error && <div className="banner danger">{error}</div>}

      {!capturing && frames.length === 0 ? (
        <Empty
          title="Capture is off"
          hint="Start a capture to watch the raw device-protocol frames in both directions. It stops on its own after 15 minutes — a trace left running across a fleet is its own outage."
        />
      ) : (
        <>
          <div className="field-hint" style={{ padding: '8px 14px' }}>
            Frames are shown verbatim, including <code>sms.send</code> and <code>sms.received</code> PDU payloads —
            those decode back to message text and full recipient numbers. Starting a capture is audited.
          </div>
          <div className="table-wrap" style={{ maxHeight: 460, overflow: 'auto' }}>
            <table>
              <tbody>
                {frames.length === 0 ? (
                  <tr>
                    <td className="faint small" style={{ padding: 14 }}>
                      Capturing — waiting for the first frame. A heartbeat arrives every 20 seconds.
                    </td>
                  </tr>
                ) : (
                  frames.map((f) => (
                    <tr key={f.seq} onClick={() => setExpanded(expanded === f.seq ? null : f.seq)} style={{ cursor: 'pointer' }}>
                      <td className="faint small" style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        {fmtTime(f.at)}
                      </td>
                      <td style={{ verticalAlign: 'top' }}>
                        <span className="chip" title={f.direction === 'in' ? 'device → server' : 'server → device'}>
                          {f.direction === 'in' ? '↓ in' : '↑ out'}
                        </span>
                      </td>
                      <td className="mono small" style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        {f.type}
                      </td>
                      <td className="faint small" style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                        {f.bytes} B
                      </td>
                      <td className="mono small" style={{ verticalAlign: 'top' }}>
                        {expanded === f.seq ? (
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{pretty(f.json)}</pre>
                        ) : (
                          <span className="muted">{f.json.length > 120 ? f.json.slice(0, 120) + '…' : f.json}</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  )
}

function pretty(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2)
  } catch {
    // An undecodable frame is captured verbatim precisely so it can be read as-is.
    return json
  }
}
